<template>
  <div class="edit-article-view container-fluid py-4">
    <div class="edit-article-controls row mb-3 align-items-end">
      <div class="col-md-3 col-12 mb-2 mb-md-0">
        <div class="form-group mb-0">
          <label for="language-select" class="font-weight-bold"
            >Language:</label
          >
          <select
            v-model="selectedLang"
            id="language-select"
            class="form-control"
          >
            <option v-for="lang in availableLangs" :key="lang" :value="lang">
              {{ lang }}
            </option>
          </select>
        </div>
      </div>
      <div class="col-md-3 col-12 d-flex justify-content-around">
        <button @click="newArticle" class="btn btn-secondary w-100 me-5">
          <i class="fa fa-download mr-2"></i>New Article
        </button>
        <button
          @click="downloadZip"
          :disabled="!hasChanges"
          class="btn btn-primary w-100"
        >
          <i class="fa fa-download mr-2"></i>Download zip
        </button>
      </div>
    </div>
    <div class="edit-article-main row">
      <div class="edit-article-editor col-md-6 col-12 mb-3 mb-md-0">
        <div class="card h-100">
          <div class="card-header bg-light font-weight-bold">Edit Article</div>
          <div class="card-body">
            <div class="form-group">
              <label for="edit-title" class="font-weight-bold">Title</label>
              <input
                id="edit-title"
                v-model="fields.title"
                @input="onEdit('title')"
                class="form-control"
                type="text"
              />
            </div>
            <div class="form-group">
              <label for="edit-meta" class="font-weight-bold">Meta</label>
              <input
                id="edit-meta"
                v-model="fields.meta"
                @input="onEdit('meta')"
                class="form-control"
                type="text"
              />
            </div>
            <div class="form-group">
              <label for="edit-rank" class="font-weight-bold">Rank</label>
              <input
                id="edit-rank"
                v-model="fields.rank"
                @input="onEdit('rank')"
                class="form-control"
                type="text"
              />
            </div>
            <div class="form-group">
              <label for="edit-iframe" class="font-weight-bold">Iframe</label>
              <input
                id="edit-iframe"
                v-model="fields.iframe"
                @input="onEdit('iframe')"
                class="form-control"
                type="text"
              />
            </div>
            <div class="form-group mb-0">
              <label for="edit-body" class="font-weight-bold"
                >Body (Markdown)</label
              >
              <textarea
                id="edit-body"
                v-model="fields.body"
                @input="onEdit('body')"
                rows="10"
                class="form-control"
                style="min-height: 200px; resize: vertical"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="edit-article-preview col-md-6 col-12">
        <div class="card h-100">
          <div class="card-header bg-light font-weight-bold">Preview</div>
          <div
            class="card-body preview-body overflow-auto"
            style="min-height: 400px"
          >
            <h5>{{ fields.title }}</h5>
            <div class="mb-2 text-muted">
              <strong>Meta:</strong> {{ fields.meta }}
            </div>
            <div v-if="fields.rank" class="mb-2">
              <strong>Rank:</strong> {{ fields.rank }}
            </div>
            <markdown-it-vue :content="fields.body" />
            <div v-if="fields.iframe" class="mt-4">
              <label class="font-weight-bold mb-2">Iframe Preview:</label>
              <div class="embed-responsive embed-responsive-16by9">
                <iframe
                  class="embed-responsive-item"
                  :src="fields.iframe"
                  allowfullscreen
                  style="
                    width: 100%;
                    min-height: 300px;
                    border: 1px solid #eee;
                    border-radius: 4px;
                  "
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import JSZip from 'jszip'
import { saveAs } from 'file-saver'

// Helper to dynamically import locale files
const localeImports = {
  en: () => import('../locales/en.json'),
  sv: () => import('../locales/sv.json'),
  da: () => import('../locales/da.json'),
  fi: () => import('../locales/fi.json'),
  no: () => import('../locales/no.json'),
  'zh-CN': () => import('../locales/zh-CN.json')
}

export default {
  name: 'EditArticle',

  data() {
    return {
      availableLangs: Object.keys(localeImports),
      selectedLang: 'en',
      locales: {}, // { lang: localeData }
      originalFields: {}, // { lang: { body, meta, title, rank } }
      editedFields: {}, // { lang: { body, meta, title, rank } }
      fields: { body: '', meta: '', title: '', rank: '', iframe: '' },
      loading: false,
      isNewArticle: false
    }
  },
  computed: {
    hasChanges() {
      return Object.keys(this.editedFields).length > 0
    }
  },
  watch: {
    selectedLang: 'loadArticleFields'
  },
  created() {
    this.loadAllLocales()
  },
  methods: {
    async loadAllLocales() {
      this.loading = true
      const { section, article } = this.$route.params
      for (const lang of this.availableLangs) {
        try {
          const mod = await localeImports[lang]()
          this.locales[lang] = mod.default
          // Traverse to the article fields
          const art =
            this.locales[lang]?.home?.sections?.[section]?.articles?.[
              article
            ] ||
            this.locales[lang]?.home?.sections?.[parseInt(section)]?.articles?.[
              parseInt(article)
            ] ||
            {}
          this.originalFields[lang] = {
            body: art.body || '',
            meta: art.meta || '',
            title: art.title || '',
            rank: art.rank || '',
            iframe: art.iframe || ''
          }
        } catch (e) {
          this.locales[lang] = null
          this.originalFields[lang] = {
            body: '',
            meta: '',
            title: '',
            rank: ''
          }
        }
      }
      this.loading = false
      this.loadArticleFields()
    },
    loadArticleFields() {
      const orig = this.originalFields[this.selectedLang] || {
        body: '',
        meta: '',
        title: '',
        rank: '',
        iframe: ''
      }
      const edit = this.editedFields[this.selectedLang]
      this.fields = { ...orig, ...edit }
    },
    onEdit(field) {
      // Save changes for the current language
      const orig = this.originalFields[this.selectedLang] || {}
      const changed = {
        body: this.fields.body,
        meta: this.fields.meta,
        title: this.fields.title,
        rank: this.fields.rank,
        iframe: this.fields.iframe
      }
      // Only store if any field is changed
      if (
        changed.body !== orig.body ||
        changed.meta !== orig.meta ||
        changed.title !== orig.title ||
        changed.rank !== orig.rank
      ) {
        this.editedFields = {
          ...this.editedFields,
          [this.selectedLang]: { ...changed }
        }
      } else {
        const { [this.selectedLang]: _, ...rest } = this.editedFields
        this.editedFields = rest
      }
    },
    newArticle() {
      // Clear all fields for all languages and reset editor for new article
      this.editedFields = {}
      for (const lang of this.availableLangs) {
        this.editedFields[lang] = {
          body: '',
          meta: '',
          title: '',
          rank: '',
          iframe: ''
        }
      }
      this.selectedLang = this.availableLangs[0]
      this.isNewArticle = true
      this.loadArticleFields()
    },
    async downloadZip() {
      const zip = new JSZip()
      for (const lang of Object.keys(this.editedFields)) {
        const locale = JSON.parse(JSON.stringify(this.locales[lang]))
        const { section, article } = this.$route.params
        let sectionObj =
          locale?.home?.sections?.[section] ||
          locale?.home?.sections?.[parseInt(section)]
        if (!sectionObj) {
          if (!locale.home) locale.home = {}
          if (!locale.home.sections) locale.home.sections = {}
          sectionObj = { articles: {} }
          locale.home.sections[section] = sectionObj
        }
        if (!sectionObj.articles) sectionObj.articles = {}

        let targetArticleNum
        if (this.isNewArticle) {
          const articleKeys = Object.keys(sectionObj.articles)
            .map((k) => parseInt(k))
            .filter((k) => !isNaN(k))
          targetArticleNum =
            articleKeys.length > 0 ? Math.max(...articleKeys) + 1 : 0
        } else {
          targetArticleNum = isNaN(parseInt(article)) ? 0 : parseInt(article)
        }

        // Merge edited fields into the original article to preserve all properties
        const originalArticle = sectionObj.articles[targetArticleNum] || {}
        // If iframe is empty string, remove it from the export
        const merged = { ...originalArticle, ...this.editedFields[lang] }
        if (merged.iframe === '') {
          delete merged.iframe
        }
        sectionObj.articles[targetArticleNum] = merged

        // Improved JS serializer for locale files
        function isValidIdentifier(key) {
          return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key)
        }
        function jsString(val) {
          if (typeof val !== 'string') return val
          // Escape backslashes, single quotes, and newlines as \n
          return (
            "'" +
            val
              .replace(/\\/g, '\\\\')
              .replace(/'/g, "\\'")
              .replace(/\r?\n/g, '\\n') +
            "'"
          )
        }
        function jsKey(key) {
          // Always quote numeric keys and invalid identifiers
          return isValidIdentifier(key) ? key : jsString(key)
        }
        function serialize(obj, indent = 0) {
          const pad = '  '.repeat(indent)
          const padNext = '  '.repeat(indent + 1)
          if (obj === null) return 'null'
          if (Array.isArray(obj)) {
            if (obj.length === 0) return '[]'
            return (
              '[\n' +
              obj.map((v) => padNext + serialize(v, indent + 1)).join(',\n') +
              '\n' +
              pad +
              ']'
            )
          }
          if (typeof obj === 'object') {
            const keys = Object.keys(obj)
            if (keys.length === 0) return '{}'
            return (
              '{\n' +
              keys
                .map(
                  (k) =>
                    padNext + jsKey(k) + ': ' + serialize(obj[k], indent + 1)
                )
                .join(',\n') +
              '\n' +
              pad +
              '}'
            )
          }
          if (typeof obj === 'string') return jsString(obj)
          return String(obj)
        }
        const jsContent = 'export default ' + serialize(locale, 0)
        zip.file(`${lang}.js`, jsContent)
      }
      this.isNewArticle = false
      const blob = await zip.generateAsync({ type: 'blob' })
      saveAs(blob, 'helpcenter-article.zip')
    }
  }
}
</script>

<style scoped>
.edit-article-view {
  /* Bootstrap container-fluid already used */
}
.edit-article-controls label {
  font-size: 1rem;
}
.edit-article-main .card {
  min-height: 420px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}
.edit-article-main .card-header {
  font-size: 1.1rem;
}
.preview-body {
  background: #fafafa;
  border-radius: 0 0 0.25rem 0.25rem;
  word-break: break-word;
}
.form-control:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.1);
}
</style>
